// @ts-strict-ignore
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import {
  ErrorBoundary,
  Icon,
  IconName,
  JourneyBadge,
  Spinner,
} from '@usealloy/component-library';

import { DataTypes } from '~/analytics/journey-analytics/dashboard/filters/filtersConfiguration';
import { snakeToCaps, truncate } from '~/helpers/formatting';
import { isDropdownOptionSelected } from '~/queries/helpers';
import OptionBadge from '~/queries/OptionBadge';

import DropdownToggleLabel from './DropdownToggleLabel';
import { DropdownMenuInputWrapper, DropdownMenuScrollerContainer } from './styles';
import log from '~/helpers/logging';

export type Facet = {
  default_value?: string;
  dependentFilters?: string[];
  disabledWhenNoOptions?: boolean;
  disabled?: boolean;
  dropdown_label: Option;
  filter_type?: string;
  has_error?: boolean;
  key: string;
  label?: string;
  lowerBound?: number;
  max?: number;
  min?: number;
  multivalue?: string;
  option_label?: string;
  options: Option[];
  search?: boolean;
  upperBound?: number;
  is_loading?: boolean;
};

export type Option = {
  extra?: string;
  fa?: any;
  hidden?: boolean;
  label: string;
  option_label?: string;
  pk?: string;
  value: string;
  outcome_color?: string;
  icon_name?: IconName;
};

export type Props = {
  additionalComponent?: Function;
  boundariesElement: JSX.Element | null;
  facet: Facet;
  filterBy: any;
  hideNormalDropdown?: boolean;
  isOpen?: boolean;
  updateFilterBy: Function;
};

function DropdownFacet({
  additionalComponent = null,
  boundariesElement,
  facet,
  filterBy,
  hideNormalDropdown,
  isOpen = undefined,
  updateFilterBy,
}: Props) {
  const searchBar = useRef(null);
  const [expanded, setExpanded] = useState(false);

  const displayBadges = facet.key === 'journeyApplicationStatus';

  const toggleExpanded = event => {
    const { classList } = event.currentTarget;
    if (!classList || !classList.contains('no-toggle')) setExpanded(!expanded);
  };
  useEffect(() => { if (isOpen !== undefined) setExpanded(isOpen); }, [isOpen]);

  const [searchInput, setSearchInput] = useState('');
  const handleUpdateSearchInput = event => setSearchInput(event.target.value);
  const clearDependentFilters = dependentFilters => {
    if (dependentFilters) {
      dependentFilters.forEach(dependentFilter => {
        updateFilterBy(dependentFilter, 'all');
      });
    }
  };

  const handleFilterByChangeClosure = (key, value) => event => {
    event.stopPropagation();
    updateFilterBy(key, value);
    clearDependentFilters(facet.dependentFilters);
    if (searchInput) setSearchInput('');
  };

  // If 'default_value' is an available prop use that
  // otherwise, use 'dropdown_label.value'
  // The reasoning is because this component gets its data/state from
  // two different endpoints from the Evaluations and Application Queue pages
  const defaultValue = facet?.default_value ?? facet?.dropdown_label?.value;
  let selectedValue = filterBy[facet.key];

  if (Array.isArray(selectedValue)) {
    [, , selectedValue] = selectedValue;
  }

  const filterType = (
    facet.multivalue && selectedValue
      ? Object.keys(selectedValue)[0]
      : false
  );

  let values = [defaultValue];

  if (![undefined, null].includes(selectedValue)) {
    values = (
      facet.multivalue && filterType
        ? selectedValue[filterType]
        : [selectedValue]
    );
  }

  const label = facet?.option_label ?? facet.label;
  // NOTE: We want to change this later when we use the CL <Badge /> everywhere
  // but for now we just want to target the Outcome facet dropdown
  const textProps = (
    facet.key === 'outcome' || facet.key === 'journeyApplicationStatus'
      ? {
        style: {
          lineHeight: '22px',
          /**
           * `values[0]` is passed as the `value` prop to the `DetailedOptionLabel`
           * component which is where these `textProps` are used
           */
          marginLeft: values?.[0] === 'all' ? '0' : '0.25rem',
          marginTop: '1px',
          verticalAlign: 'super',
        },
      }
      : {}
  );

  const wrapperProps = (
    facet.key === 'outcome' || facet.key === 'journeyApplicationStatus'
      ? {
        style: {
          display: 'inline-flex',
          alignItems: 'center',
        },
      }
      : {}
  );

  const popperModifiers = useMemo(() => {
    const defaultPopperModifier = {
      preventOverflow: { enabled: false },
      computeStyle: { enabled: false }, // disabling compute style clears popper behavior
    };
    if (!boundariesElement) return defaultPopperModifier;
    return {
      ...defaultPopperModifier,
      preventOverflow: { boundariesElement: boundariesElement as unknown as Element },
    };
  }, [boundariesElement]);

  return (
    <div>
      {label && <span className="small-text">{label}</span>}

      <Dropdown
        className={`${facet.filter_type === DataTypes.Range ? 'range-filter ' : ''}facet`}
        isOpen={expanded}
        toggle={toggleExpanded}
      >
        <DropdownToggle
          className="secondary nocase"
          data-testid={`facet-menu-toggle-${facet.label}`}
          disabled={facet.disabled || (facet.disabledWhenNoOptions && facet.options.length === 0)}
        >
          <DropdownToggleLabel
            facet={facet}
            filterType={filterType}
            textProps={textProps}
            values={values}
            wrapperProps={wrapperProps}
          />
        </DropdownToggle>

        <DropdownMenu
          className="small-text"
          modifiers={popperModifiers}
        >
          {facet.search && (
            <DropdownMenuInputWrapper>
              <input
                data-testid="dropdown-menu-input"
                ref={searchBar}
                type="text"
                placeholder="Search..."
                value={searchInput}
                onChange={handleUpdateSearchInput}
                style={{ width: '100%', padding: '0 8px' }}
              />
            </DropdownMenuInputWrapper>
          )}

          {facet.has_error && (
            <DropdownItem
              data-testid="dropdown-menu-has-error"
              disabled
              style={{ textAlign: 'center' }}
            >
              <Icon
                color="red"
                icon={IconName.Error}
                style={{ pointerEvents: 'none' }}
              />
            </DropdownItem>
          )}

          {facet.is_loading && (
            <DropdownItem
              data-testid="dropdown-menu-loading"
              disabled
              style={{ textAlign: 'center' }}
            >
              <Spinner />
            </DropdownItem>
          )}
          <ErrorBoundary
            withResetChildren
            loggingCallback={error => {
              log('error', 'Error in Dropdown Facet', { url: window.location.pathname }, error);
            }}
          >
            <DropdownMenuScrollerContainer data-testid="dropdown-menu-scroller">
              {
                !facet.is_loading
                && !facet.has_error
                && !hideNormalDropdown
                && [facet.dropdown_label, ...facet.options]
                  .filter(option => (
                    !searchInput
                    || typeof searchInput !== 'string'
                    || option.label.toLowerCase().includes(searchInput.toLowerCase())
                    || option.value.toLowerCase().includes(searchInput.toLowerCase())
                  ))
                  .map(option => {
                    const selected = isDropdownOptionSelected(
                      facet.default_value,
                      values,
                      option.value,
                    );
                    const isAllOption = option.label === 'All';
                    const displayBadge = displayBadges && !isAllOption;
                    const props = displayBadge
                      ? {
                        ...(option.icon_name && { outcomeIcon: option.icon_name }),
                        ...(option.outcome_color && {
                          style: {
                            backgroundColor:
                              option.outcome_color,
                          },
                        }),
                      }
                      : {};

                    return !option.hidden && (
                      <DropdownItem
                        key={`${(option.pk || option.value)}-${uuidv4()}`}
                        data-testid={`facet-menu-toggle-${facet.label}-dropdown-item-${option.pk || option.value}`}
                        data-dd-action-name={`alloydd-dropdown-${facet.label}-option-${option.pk || option.value}`}
                        className={`flex start h-space-4 no-wrap no-toggle ${!displayBadge && selected ? 'selected' : ''}`}
                        onClick={handleFilterByChangeClosure(facet.key, option.value)}
                      >
                        {facet.multivalue && (
                          <input
                            type="checkbox"
                            data-testid={`facet-menu-toggle-input-check-${option.value}`}
                            checked={selected}
                            onChange={() => handleFilterByChangeClosure(facet.key, option.value)}
                          />
                        )}

                        {displayBadge ? (
                          <JourneyBadge
                            outcome={option.value}
                            {...props}
                          >
                            {option.label}
                          </JourneyBadge>
                        ) : (
                          <>
                            {option.option_label && <span>{`${snakeToCaps(option.option_label)}: `}</span>}

                            {option.fa && (
                              <OptionBadge
                                displayOutcome={facet.key === 'outcome'}
                                facet={facet}
                                option={option}
                              />
                            )}

                            {facet.key === 'outcome'
                              ? (isAllOption && option.label && (
                                <span>{truncate(option.label, 75)}</span>
                              ))
                              : (option.label && <span>{truncate(option.label, 75)}</span>)}

                            {option.extra && <span>{option.extra}</span>}
                          </>
                        )}
                      </DropdownItem>
                    );
                  })
              }
            </DropdownMenuScrollerContainer>
          </ErrorBoundary>
          {additionalComponent && additionalComponent()}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

export default memo(DropdownFacet);
